import React, { useState, useEffect, useCallback } from 'react';
import BasePage from './BasePage';
import {
    Typography, Table, Tag, Space, Button, Modal, Form, Input,
    Select, Popconfirm, message, Card, Row, Col, Divider, Tooltip,
    Badge, Alert,
} from 'antd';
import {
    UserOutlined, PlusOutlined, DeleteOutlined,
    LinkOutlined, ReloadOutlined, TeamOutlined, CrownOutlined,
    LockOutlined, MailOutlined, CheckCircleOutlined, StopOutlined,
    KeyOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { usuariosService } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

// ─── helpers ─────────────────────────────────────────────────────────────────
const roleLabel = (role) => {
    switch (role) {
        case 'ADMIN':  return { label: 'Administrador', color: 'volcano' };
        case 'CLIENT': return { label: 'Usuário',        color: 'geekblue' };
        default:       return { label: role,              color: 'default' };
    }
};

const statusBadge = (status) =>
    status === 'ATIVO'
        ? <Badge status="success" text={<Text style={{ color: '#52c41a', fontWeight: 500 }}>Ativo</Text>} />
        : <Badge status="error"   text={<Text style={{ color: '#ff4d4f', fontWeight: 500 }}>Inativo</Text>} />;

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// ─── componente principal ─────────────────────────────────────────────────────
const UserManagementPage = () => {
    const [usuarios, setUsuarios]         = useState([]);
    const [empresas, setEmpresas]         = useState([]);
    const [loading, setLoading]           = useState(false);
    const [createOpen, setCreateOpen]     = useState(false);
    const [resetOpen, setResetOpen]       = useState(false);
    const [resetTarget, setResetTarget]   = useState(null);   // { id, username }
    const [assocLoading, setAssocLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({});

    const [createForm] = Form.useForm();
    const [resetForm]  = Form.useForm();
    const [assocForm]  = Form.useForm();

    // Usuário logado — para verificar escopo
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); }
        catch { return {}; }
    })();
    const isSuperAdmin = !currentUser.empresaClienteId;

    // ── carrega dados ──────────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [uRes, eRes] = await Promise.all([
                usuariosService.getAll(),
                usuariosService.getEmpresas(),
            ]);
            if (uRes.success) setUsuarios(uRes.data);
            if (eRes.success) setEmpresas(eRes.data);
        } catch {
            message.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── helpers de loading por ação ────────────────────────────────────────────
    const withLoading = async (key, fn) => {
        setActionLoading(p => ({ ...p, [key]: true }));
        try { await fn(); }
        finally { setActionLoading(p => ({ ...p, [key]: false })); }
    };

    // ── ações ──────────────────────────────────────────────────────────────────
    const handleToggleStatus = (record) =>
        withLoading(`status_${record.id}`, async () => {
            const next = record.status === 'ATIVO' ? 'INATIVO' : 'ATIVO';
            try {
                await usuariosService.updateStatus(record.id, next);
                message.success(next === 'ATIVO' ? 'Usuário ativado' : 'Usuário desativado');
                fetchAll();
            } catch { message.error('Erro ao atualizar status'); }
        });

    const handleToggleRole = (record) =>
        withLoading(`role_${record.id}`, async () => {
            const next = record.role === 'ADMIN' ? 'CLIENT' : 'ADMIN';
            try {
                await usuariosService.updateRole(record.id, next);
                message.success('Papel atualizado');
                fetchAll();
            } catch { message.error('Erro ao atualizar papel'); }
        });

    const handleDelete = async (id) => {
        try {
            await usuariosService.remove(id);
            message.success('Usuário removido');
            fetchAll();
        } catch { message.error('Erro ao remover usuário'); }
    };

    const handleCreate = async (values) => {
        try {
            await usuariosService.create(values);
            message.success('Usuário criado com sucesso!');
            setCreateOpen(false);
            createForm.resetFields();
            fetchAll();
        } catch (e) {
            const msg = e?.response?.data?.message || 'Erro ao criar usuário';
            message.error(Array.isArray(msg) ? msg.join(', ') : msg);
        }
    };

    const handleResetPassword = async (values) => {
        try {
            await usuariosService.resetPassword(resetTarget.id, values.password);
            message.success(`Senha de "${resetTarget.username}" redefinida com sucesso`);
            setResetOpen(false);
            resetForm.resetFields();
            setResetTarget(null);
        } catch (e) {
            message.error(e?.response?.data?.message || 'Erro ao redefinir senha');
        }
    };

    const handleAssociate = async (values) => {
        setAssocLoading(true);
        try {
            const res = await usuariosService.associate(values.email, values.empresaClienteId);
            message.success(res.message || 'Associação realizada com sucesso');
            assocForm.resetFields();
            fetchAll();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Usuário ou empresa não encontrado(a)');
        } finally {
            setAssocLoading(false);
        }
    };

    const openResetModal = (record) => {
        setResetTarget({ id: record.id, username: record.username });
        resetForm.resetFields();
        setResetOpen(true);
    };

    // ── colunas ────────────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Cód.',
            dataIndex: 'id',
            key: 'id',
            width: 60,
            align: 'center',
            render: v => <Text type="secondary" style={{ fontSize: 12 }}>#{v}</Text>,
        },
        {
            title: 'Login / E-mail',
            dataIndex: 'username',
            key: 'username',
            render: (v, r) => (
                <Space>
                    <div
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: r.role === 'ADMIN' ? '#fff2e8' : '#e6f4ff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${r.role === 'ADMIN' ? '#FB8500' : '#91caff'}`,
                            flexShrink: 0,
                        }}
                    >
                        <UserOutlined style={{ color: r.role === 'ADMIN' ? '#FB8500' : '#1677ff', fontSize: 16 }} />
                    </div>
                    <div>
                        <Text strong style={{ display: 'block' }}>{v}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Empresa',
            dataIndex: 'empresaNome',
            key: 'empresaNome',
            render: v => v
                ? <Tag color="geekblue" style={{ fontWeight: 500 }}>{v}</Tag>
                : <Tag color="orange" style={{ fontWeight: 500 }}>Heliosfera (global)</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            align: 'center',
            render: v => statusBadge(v),
        },
        {
            title: 'Papel',
            dataIndex: 'role',
            key: 'role',
            width: 130,
            align: 'center',
            render: v => {
                const { label, color } = roleLabel(v);
                return <Tag color={color} style={{ fontWeight: 600 }}>{label}</Tag>;
            },
        },
        {
            title: 'Última modificação',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 155,
            render: v => <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(v)}</Text>,
        },
        {
            title: 'Ações',
            key: 'acoes',
            width: 160,
            align: 'center',
            render: (_, record) => (
                <Space size={4}>
                    {/* Alternar Admin / Cliente */}
                    <Tooltip title={record.role === 'ADMIN' ? 'Remover permissão admin' : 'Tornar administrador'}>
                        <Button
                            size="small"
                            icon={<CrownOutlined />}
                            loading={!!actionLoading[`role_${record.id}`]}
                            type={record.role === 'ADMIN' ? 'primary' : 'default'}
                            style={record.role === 'ADMIN'
                                ? { background: '#FB8500', borderColor: '#FB8500' }
                                : {}}
                            onClick={() => handleToggleRole(record)}
                        />
                    </Tooltip>

                    {/* Ativar / Desativar */}
                    <Tooltip title={record.status === 'ATIVO' ? 'Desativar usuário' : 'Ativar usuário'}>
                        <Button
                            size="small"
                            icon={record.status === 'ATIVO' ? <StopOutlined /> : <CheckCircleOutlined />}
                            loading={!!actionLoading[`status_${record.id}`]}
                            danger={record.status === 'ATIVO'}
                            type={record.status !== 'ATIVO' ? 'primary' : 'default'}
                            style={record.status !== 'ATIVO'
                                ? { background: '#52c41a', borderColor: '#52c41a' }
                                : {}}
                            onClick={() => handleToggleStatus(record)}
                        />
                    </Tooltip>

                    {/* Redefinir Senha */}
                    <Tooltip title="Redefinir senha">
                        <Button
                            size="small"
                            icon={<KeyOutlined />}
                            onClick={() => openResetModal(record)}
                        />
                    </Tooltip>

                    {/* Excluir */}
                    <Popconfirm
                        title="Remover usuário?"
                        description="Esta ação não pode ser desfeita."
                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                        okText="Sim, remover"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Tooltip title="Excluir usuário">
                            <Button size="small" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ── render ─────────────────────────────────────────────────────────────────
    return (
        <BasePage>
            <div style={{ padding: '0 0 40px' }}>

                {/* Cabeçalho */}
                <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
                    <Col>
                        <Space align="center">
                            <div style={{
                                width: 44, height: 44, borderRadius: 10,
                                background: 'linear-gradient(135deg,#FB8500,#ff9f00)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <TeamOutlined style={{ fontSize: 22, color: '#fff' }} />
                            </div>
                            <div>
                                <Title level={3} style={{ margin: 0 }}>Gerenciamento de Usuários</Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    {usuarios.length} usuário(s) cadastrado(s)
                                    {!isSuperAdmin && ` · ${currentUser.empresaClienteNome || 'sua empresa'}`}
                                </Text>
                            </div>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>
                                Atualizar
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                style={{ background: '#FB8500', borderColor: '#FB8500' }}
                                onClick={() => setCreateOpen(true)}
                            >
                                Novo Usuário
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* Aviso de escopo */}
                {!isSuperAdmin && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={`Você está gerenciando usuários da empresa: ${currentUser.empresaClienteNome || '—'}`}
                    />
                )}

                {/* Tabela */}
                <Card bodyStyle={{ padding: 0 }}>
                    <Table
                        columns={columns}
                        dataSource={usuarios}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 10, showTotal: t => `${t} usuário(s)` }}
                        size="middle"
                        style={{ borderRadius: 8, overflow: 'hidden' }}
                        rowClassName={(r) => r.status === 'INATIVO' ? 'user-row-inactive' : ''}
                    />
                </Card>

                <Divider />

                {/* Associar usuário a empresa */}
                <Card
                    title={
                        <Space>
                            <LinkOutlined style={{ color: '#FB8500' }} />
                            <span>Associar Usuário a uma Empresa</span>
                        </Space>
                    }
                    style={{ maxWidth: 740 }}
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                        Informe o e-mail de um usuário já cadastrado e selecione a empresa para vinculá-lo.
                    </Text>
                    <Form form={assocForm} layout="vertical" onFinish={handleAssociate}>
                        <Row gutter={12}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="email"
                                    label="E-mail do usuário"
                                    rules={[{ required: true, type: 'email', message: 'E-mail válido obrigatório' }]}
                                >
                                    <Input prefix={<MailOutlined />} placeholder="usuario@exemplo.com" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="empresaClienteId"
                                    label="Empresa"
                                    rules={[{ required: true, message: 'Selecione a empresa' }]}
                                >
                                    <Select placeholder="Selecione uma empresa">
                                        {empresas.map(e => (
                                            <Option key={e.id} value={e.id}>{e.nome}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={assocLoading}
                                icon={<LinkOutlined />}
                                style={{ background: '#FB8500', borderColor: '#FB8500' }}
                            >
                                Associar
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>

            {/* ── Modal: Criar Usuário ──────────────────────────────────────────── */}
            <Modal
                title={
                    <Space>
                        <PlusOutlined style={{ color: '#FB8500' }} />
                        <span>Novo Usuário</span>
                    </Space>
                }
                open={createOpen}
                onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
                footer={null}
                width={500}
                centered
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                    style={{ marginTop: 16 }}
                >
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="username"
                                label="Nome / Login"
                                rules={[{ required: true, message: 'Nome obrigatório' }]}
                            >
                                <Input prefix={<UserOutlined />} placeholder="Ex.: João Silva" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="role" label="Papel" initialValue="CLIENT">
                                <Select>
                                    <Option value="CLIENT">Usuário</Option>
                                    <Option value="ADMIN">Administrador</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="email"
                        label="E-mail"
                        rules={[{ required: true, type: 'email', message: 'E-mail válido obrigatório' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="email@exemplo.com" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Senha inicial"
                        rules={[
                            { required: true, message: 'Senha obrigatória' },
                            { min: 6, message: 'Mínimo 6 caracteres' },
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Mínimo 6 caracteres" />
                    </Form.Item>

                    <Form.Item name="empresaClienteId" label="Empresa">
                        <Select
                            allowClear
                            placeholder={isSuperAdmin ? 'Sem empresa (admin global)' : empresas[0]?.nome}
                        >
                            {empresas.map(e => (
                                <Option key={e.id} value={e.id}>{e.nome}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setCreateOpen(false); createForm.resetFields(); }}>
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{ background: '#FB8500', borderColor: '#FB8500' }}
                            >
                                Criar Usuário
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* ── Modal: Redefinir Senha ────────────────────────────────────────── */}
            <Modal
                title={
                    <Space>
                        <KeyOutlined style={{ color: '#FB8500' }} />
                        <span>
                            Redefinir senha
                            {resetTarget ? ` — ${resetTarget.username}` : ''}
                        </span>
                    </Space>
                }
                open={resetOpen}
                onCancel={() => { setResetOpen(false); resetForm.resetFields(); setResetTarget(null); }}
                footer={null}
                width={400}
                centered
            >
                <Form
                    form={resetForm}
                    layout="vertical"
                    onFinish={handleResetPassword}
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        name="password"
                        label="Nova senha"
                        rules={[
                            { required: true, message: 'Senha obrigatória' },
                            { min: 6, message: 'Mínimo 6 caracteres' },
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Mínimo 6 caracteres" />
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        label="Confirmar nova senha"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Confirmação obrigatória' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value)
                                        return Promise.resolve();
                                    return Promise.reject(new Error('As senhas não coincidem'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Repita a nova senha" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setResetOpen(false); resetForm.resetFields(); setResetTarget(null); }}>
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<KeyOutlined />}
                                style={{ background: '#FB8500', borderColor: '#FB8500' }}
                            >
                                Redefinir
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Estilo para linhas inativas */}
            <style>{`
                .user-row-inactive td { opacity: 0.55; }
            `}</style>
        </BasePage>
    );
};

export default UserManagementPage;
